







































import { computed, defineComponent, onUnmounted, ref, watch } from "@vue/composition-api";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { StoryStatistic } from "@scrile/api-provider/dist/api/StoriesProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { Story } from "@scrile/api-provider/dist/projects/scrileConnect/StoriesProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { StoryPublisherWithStories, ViewArchivedStoriesSettings, ViewStoriesSettings } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseSpinner from "@/components/BaseSpinner";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";
import StoriesStatistic from "@/components/StoriesStatistic";
import { StoryStatisticType } from "@/components/StoriesStatistic/StoriesStatistic.vue";
import StoriesView from "./components/StoriesView";

export default defineComponent({
  name: "Stories",
  components: { StoriesStatistic, StoriesView, BaseConfirm, BaseButton, BaseSpinner },
  setup(_, { root }) {
    let resolveFollowing = () => {};
    const { authUser, authUserStrict, isCreator } = useAuthUser();
    const emptyStoriesSettings = {
      storyPublishers: [],
      storyIds: undefined,
      currentUserId: "",
      currentStoryId: undefined,
      routeSupport: false,
      feeds: [],
    };
    const loading = ref(false);
    const showStories = ref(false);
    const activeStatisticTab = ref<StoryStatisticType | null>(null);
    const showFollowConfirm = ref(false);
    const storiesSettings = ref<ViewStoriesSettings>();
    const feeds = ref<ContentFeed[]>([]);
    const storyStatistic = ref<StoryStatistic[]>([]);
    let archivedStories = ref<Story[]>([]);
    let storiesList = ref<Story[]>([]);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const followingsIds = ref<string[]>([]);
    const currentUserId = ref("");
    const currentStoryId = ref("");
    const callbackClose = ref<() => Promise<void> | void>();
    const getNextPage = ref<() => Promise<void>>();
    const hasFeeds = computed(() => !!feeds.value.length);
    const stories = computed<StoryPublisherWithStories[]>(() => {
      if (!storiesSettings.value?.storyPublishers || !hasFeeds.value) return [];
      return storiesSettings.value?.storyPublishers.map(item => ({
        ...item,
        feeds: feeds.value
          .filter(feed => feed.userId === item.userId)
          .map(filteredFeed => {
            const overlay = storiesList.value.find(item => item.contentFeed?.id === filteredFeed.id)?.overlay;
            return {
              ...filteredFeed,
              ...(overlay ? { overlay } : {}),
              ...(authUser.value?.id === currentUserId.value
                ? { statistics: storyStatistic.value?.find(stat => stat.storyId === filteredFeed.id) }
                : {}),
            };
          }),
      }));
    });
    const myStories = computed(() => {
      const myStories = stories.value.filter(s => s.user.id === authUser.value?.id);
      return myStories.length ? myStories[0].feeds : [];
    });
    const currentUserIndex = computed(() => stories.value?.findIndex(item => item.user.id === currentUserId.value));
    const currentStoryIndex = computed(() => {
      const currentStory = stories.value.find(item => item.user.id === currentUserId.value);
      if (!currentStory) return 0;
      const index = currentStory.feeds.findIndex(feed => feed.id === storiesSettings.value?.currentStoryId);
      return index > -1 ? index : 0;
    });
    const titleFollowConfirm = computed(() =>
      $t("Follow {0} to see their stories.", [
        storiesSettings.value?.storyPublishers.find(item => item.userId === currentUserId.value)?.user.username,
      ])
    );

    const close = () => {
      const backValue = storiesSettings.value?.back;
      showStories.value = false;
      activeStatisticTab.value = null;
      storiesSettings.value = emptyStoriesSettings;
      feeds.value = [];
      getNextPage.value = undefined;
      callbackClose.value && callbackClose.value();
      if (backValue) {
        root.$router.replace(backValue);
      }
    };

    const getUserStories = async (force: boolean = false) => {
      if (
        (!currentUserId.value && (!storiesSettings.value?.storyIds || storiesSettings.value?.storyIds.length)) ||
        (!force && feeds.value.some(i => i.userId === currentUserId.value))
      )
        return;
      try {
        loading.value = true;
        const response = await providers.StoriesProvider.searchStories({
          data: {
            ids: storiesSettings.value?.storyIds,
            ownerIds: [currentUserId.value],
            archived: false,
            page: 0,
            sort: "CREATE_TIME,ASC",
            size: 999,
          },
        });
        storiesList.value = response.result;
        if (force) {
          feeds.value = feeds.value.filter(feed => feed.userId !== currentUserId.value);
        }
        if (storiesList.value.length > 0) {
          const newFeeds = storiesList.value
            .filter(i => i.contentFeed !== null)
            .map(i => i.contentFeed) as ContentFeed[];
          const newStatistic = storiesList.value
            .filter(i => i.statistic !== null)
            .map(i => i.statistic) as StoryStatistic[];
          storyStatistic.value = [...storyStatistic.value, ...newStatistic];
          feeds.value = [...feeds.value, ...newFeeds];
        }
      } finally {
        loading.value = false;
      }
    };

    const getSubscriptionLevels = async () => {
      if (!authUser.value || !isCreator.value) return;
      subscriptionLevels.value = await providers.SubscriptionLevelProvider.find({ userIds: [authUser.value.id] });
    };

    const getFollowings = async () => {
      if (!authUser.value) return;
      followingsIds.value = (
        await providers.FollowingsProvider.searchFollowings({
          ids: storiesSettings.value?.storyPublishers?.map(item => item.userId) || [],
        })
      ).map(i => i.userId);
    };

    const deleteHandler = (storyId: string) => {
      feeds.value = feeds.value.filter(i => i.id !== storyId);
      if (!hasFeeds.value) close();
    };

    const markStoryAsViewed = async (feedId: string) => {
      if (!hasFeeds.value || !authUser.value) return;
      const feed: ContentFeed | null = feeds.value.find(i => i.id === feedId) || null;
      if (!feed || !feed.purchased) return;
      await providers.StoriesProvider.updateStoryView({ data: { storyId: feedId } });
    };

    const setRoute = async (feedId: string) => {
      if (!storiesSettings.value?.routeSupport) return;
      await root.$nextTick();
      if (feedId === root.$route.query.storyId) return;
      const { name, query, params } = root.$route;
      await root.$router.replace({
        name: name || undefined,
        params,
        query: { ...query, storyId: feedId },
      });
    };

    const startViewStoryHandler = (feedId: string) => {
      currentStoryId.value = feedId;
      markStoryAsViewed(feedId);
      setRoute(feedId);
    };

    const showStoriesHandler = async (data: ViewStoriesSettings | undefined) => {
      if (!data?.storyPublishers.length) return;
      storiesSettings.value = data;
      currentUserId.value = storiesSettings.value?.currentUserId || storiesSettings.value?.storyPublishers[0].userId;
      await getFollowings();
      if (
        currentUserId.value &&
        !followingsIds.value.includes(currentUserId.value) &&
        authUser.value?.id !== currentUserId.value
      ) {
        showFollowConfirm.value = true;
        const { promise, resolve } = createDeferred();
        resolveFollowing = resolve;
        await promise;
      }
      await getUserStories();
      if (!hasFeeds.value) {
        close();
        return;
      }
      await Promise.all([getSubscriptionLevels()]);
      showStories.value = true;
      callbackClose.value = data.cb;
      pauseGlobalPlayer();
    };

    const setNewStoriesData = () => {
      const newFeeds = archivedStories.value.map(story => story.contentFeed || ({} as ContentFeed));
      if (feeds.value.length !== newFeeds.length) {
        storyStatistic.value = archivedStories.value.map(story => story.statistic as StoryStatistic);
        currentUserId.value = authUserStrict.value.id;
        feeds.value = newFeeds;
      }
    };
    const showArchivedStoriesHandler = async (data: ViewArchivedStoriesSettings | undefined) => {
      if (!data?.stories && !data?.stories.value.length) return;
      // todo need refactor
      // eslint-disable-next-line vue/no-ref-as-operand
      archivedStories = data.stories;
      // eslint-disable-next-line vue/no-ref-as-operand
      storiesList = data.stories;
      getNextPage.value = data.cb;
      storiesSettings.value = {
        storyPublishers: [
          {
            userId: archivedStories.value[0].ownerId,
            user: archivedStories.value[0].contentFeed!.user,
          },
        ],
        currentStoryId: data.currentStoryId,
      };
      setNewStoriesData();
      showStories.value = true;
    };

    const handleGetNextPage = async () => {
      if (getNextPage.value) {
        await getNextPage.value();
        setNewStoriesData();
      }
    };

    const changeUser = async (index: any, callback: (hasStory: boolean) => void) => {
      currentUserId.value = storiesSettings.value?.storyPublishers[index].userId || "";
      await getUserStories();
      if (!hasFeeds.value) {
        close();
        return;
      }
      callback && callback(hasFeeds.value && feeds.value.some(i => i.userId === currentUserId.value));
    };

    const followToUser = useAuthorisedOnly(async () => {
      if (!currentUserId.value) return;
      const response = await providers.FollowingsProvider.toggle({ id: currentUserId.value });
      if (response) followingsIds.value.push(currentUserId.value);
      resolveFollowing();
      showFollowConfirm.value = false;
    });

    const cancelFollow = () => {
      resolveFollowing();
      showFollowConfirm.value = false;
    };

    const updateStoryLike = useAuthorisedOnly(async (feedId: string) => {
      const likedResponse = await providers.LikeProvider.toggle({
        subject: { subjectId: feedId, subjectType: LikeSubjectType.CONTENT_SET },
      });
      const likedStory = feeds.value.find(feed => feed.id === feedId);
      if (likedStory) {
        likedStory.likes.liked = likedResponse;
      }
    });

    watch(showStories, showStories => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, showStories);
    });

    emitter.on<ViewStoriesSettings>(EVENTS.STORIES_SHOW, showStoriesHandler);
    emitter.on<ViewArchivedStoriesSettings>(EVENTS.ARCHIVED_STORIES_SHOW, showArchivedStoriesHandler);
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, () => getUserStories(true));
    emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, () => getUserStories(true));
    onUnmounted(() => {
      emitter.off<ViewStoriesSettings>(EVENTS.STORIES_SHOW, showStoriesHandler);
      emitter.off<ViewArchivedStoriesSettings>(EVENTS.ARCHIVED_STORIES_SHOW, showArchivedStoriesHandler);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, () => getUserStories(true));
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, () => getUserStories(true));
    });

    return {
      loading,
      showStories,
      activeStatisticTab,
      myStories,
      showFollowConfirm,
      titleFollowConfirm,
      stories,
      currentUserIndex,
      currentStoryIndex,
      subscriptionLevels,
      currentStoryId,
      changeUser,
      deleteHandler,
      cancelFollow,
      startViewStoryHandler,
      followToUser,
      close,
      updateStoryLike,
      handleGetNextPage,
    };
  },
});
